import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import mapThemes from './methods/mapThemes'
import updatePins from './methods/updatePins'
import updateBounds from './methods/updateBounds'
import initMap from './methods/initMap'

export default {
    name: 'GoogleMapsWithMarkers',
    props: {
        // optional clickhandler when user clicks a marker
        clickLocation: {
            type: Function,
        },
        // optionnal focus points used to direct the map rendering
        focusPoints: {
            type: Array,
            default: [],
        },
        // optional data providing info on the currently highlighted or "hovered" marker
        highlightLocation: {
            type: Object,
            default: null,
        },
        // optional mouseover handler for markers
        hoverEnterLocation: {
            type: Function,
            default: null,
        },
        // optional mouseout handler for markers
        hoverLeaveLocation: {
            type: Function,
        },
        // array with lat/lng and info for markers to be rendered
        locations: {
            type: Array,
            default: [],
        },
        // apikey for google maps
        mapkey: {
            type: String,
            required: true,
        },
        // optional styles as string
        style: {
            type: String,
        },
        // theme name. Name/theme must be defined in mapThemes.js
        theme: {
            type: String,
            required: true,
        },
        isTotalkredit: {
            type: Boolean,
            default: false,
        },
        designSchema: {
            type: String,
            default: 'nykredit',
            validator: (value) => ['nykredit', 'spar-nord', 'totalkredit'].includes(value),
        },
        startLatitude: {
            type: Number,
            required: false,
        },
        startLongitude: {
            type: Number,
            required: false,
        },
        // optional initial zoom level (e.g. restored from URL params)
        startZoom: {
            type: Number,
            required: false,
        },
    },
    setup(props, { emit }) {
        const mapsScriptUrl = `https://maps.googleapis.com/maps/api/js?key=${props.mapkey}&language=da-DK&region=dk&libraries=places`
        const map = ref(null)
        const pins = ref([])
        const idleListener = ref(null)
        const zoomChangedListener = ref(null)
        const mapContainer = computed(() => document.querySelector('#mapcontainer'))
        const defaultZoom = computed(() => props.startZoom ?? 7)
        const defaultCenter = computed(() => (window.google ? new window.google.maps.LatLng(props.startLatitude ?? 56.27206, props.startLongitude ?? 10.3) : undefined))
        const currentMapTheme = ref({})
        const mapLocations = computed(() => props.locations ?? [])
        const localHighlightLocation = ref(null)
        const highlightLocation = computed({
            get() {
                return props.highlightLocation ?? localHighlightLocation.value
            },
            set(location) {
                localHighlightLocation.value = location
            },
        })

        // Emits map-moved whenever the map finishes panning or zooming.
        // URL debouncing is handled by the consumer (CenterMap).
        const emitMapMoved = () => {
            if (!map.value) return
            const center = map.value.getCenter()
            emit('map-moved', {
                lat: center.lat(),
                lng: center.lng(),
                zoom: map.value.getZoom(),
            })
        }

        const setAltTextForImg = () => {
            const allImgPins = mapContainer.value.querySelectorAll('img[draggable="false"]')
            if (!allImgPins.length) {
                setTimeout(setAltTextForImg, 50)
                return
            }
            allImgPins.forEach((img) => {
                const parent = img.parentNode
                const altText = parent.getAttribute('title')
                img.setAttribute('alt', altText)
            })
        }

        const setPins = () => {
            if (props.isTotalkredit) {
                mapLocations.value.forEach((location) => location.name = location.bankName)
            }

            pins.value = updatePins(
                props,
                mapLocations,
                pins,
                markerThemeExtractor,
                currentMapTheme,
                highlightLocation,
                map
            )

        }

        onMounted(() => {
            const loadMapScript = document.createElement('script')
            loadMapScript.type = 'text/javascript'
            loadMapScript.src = mapsScriptUrl
            loadMapScript.onload = () => {
                if (mapContainer.value && window.google) {
                    currentMapTheme.value = mapThemes[props.theme](props.designSchema)
                    map.value = initMap(mapContainer, initialMapOptions)

                    setPins()

                    if (props.focusPoints.length > 0) {
                        updateBounds(props, map, mapLocations, defaultZoom.value)
                    }

                    emit('current-zoom', map.value.getZoom())
                    setTimeout(setAltTextForImg, 50)
                    zoomChangedListener.value = map.value.addListener('zoom_changed', () => {
                        emit('current-zoom', map.value.getZoom())
                    })
                    idleListener.value = map.value.addListener('idle', emitMapMoved)
                }
            }

            document.body.appendChild(loadMapScript)
        })

        watch(mapLocations, (newLocations, oldLocations) => {
            if (!map.value) {
                return
            }
            pins.value.forEach(({ marker }) => marker.setMap(null))
            pins.value = []
            updateLocalState(props, pins)
        })

        watch(() => props.focusPoints, (newFocusPoints) => {
            if (!map.value) {
                return
            }
            if (newFocusPoints.length === 0 && mapLocations.value.length === 0) {
                resetMapToDefault()
                return
            }
            updateBounds(props, map, mapLocations, defaultZoom.value)
        })

        onUnmounted(() => {
            if (idleListener.value) {
                idleListener.value.remove()
            }
            if (zoomChangedListener.value) {
                zoomChangedListener.value.remove()
            }
        })

        const initialMapOptions = computed(() => {
            return {
                zoom: defaultZoom.value,
                center: {
                    lat: defaultCenter.value.lat(),
                    lng: defaultCenter.value.lng(),
                },
                scrollwheel: false,
                fullscreenControl: false,
                streetViewControl: false,
                zoomControl: true,
                mapTypeId: 'roadmap',
                mapTypeControlOptions: {
                    mapTypeIds: ['roadmap', 'satellite'],
                    position: window.google.maps.ControlPosition.TOP_LEFT,
                },
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.TOP_RIGHT,
                    style: window.google.maps.ZoomControlStyle.LARGE
                },
                clickableIcons: false,
                gestureHandling: 'cooperative',
                ...currentMapTheme.value.mapOptions,
            }
        })

        const markerThemeExtractor = function (highlighted, useMediumIcon, useBigIcon, brand) {
            if (useBigIcon) {
                return highlighted ? currentMapTheme.value.markerBigHighlight(brand) : currentMapTheme.value.markerBig(brand)
            } else if (useMediumIcon) {
                return highlighted ? currentMapTheme.value.markerMediumHighlight(brand) : currentMapTheme.value.markerMedium(brand)
            } else {
                return highlighted ? currentMapTheme.value.markerHighlight(brand) : currentMapTheme.value.markerDefault(brand)
            }
        }

        const resetMapToDefault = () => {
            if (map.value) {
                map.value.setCenter(defaultCenter.value)
                map.value.setZoom(defaultZoom.value)
            }
        }

        const updateLocalState = function (props, pins) {
            if (map.value !== null) {
                setPins()
            }

            if (pins.value !== null && map.value !== null) {
                if (mapLocations.value.length === 0) {
                    resetMapToDefault()
                } else {
                    updateBounds(props, map, mapLocations, defaultZoom.value)
                }
            }
        }
    },
}
